import { Stack, Typography } from '@mui/material';
import {
  createFunnelFunctionLibrary,
  deleteFunnelFunctionLibrary,
  getFunnelFunctionLibraries,
  updateFunnelFunctionLibrary,
} from 'API/linkedin-funnel-funnel-function-library';
import { Roles, ZoomLevels } from 'enums';
import AdminSubpageHeader from 'features/admin/admin-subpage-header';
import AddFunctionToLibraryDialog from 'features/admin/prompt-management/add-function-to-library-dialog';
import AddLibraryDialog from 'features/admin/prompt-management/add-library-dialog';
import DeleteFunctionDialog from 'features/admin/prompt-management/delete-function-dialog';
import DeleteLibraryDialog from 'features/admin/prompt-management/delete-library-dialog';
import EditFunctionDialog from 'features/admin/prompt-management/edit-function-dialog';
import EditLibraryDialog from 'features/admin/prompt-management/edit-library-dialog';
import PromptLibraries from 'features/admin/prompt-management/prompt-libraries';
import { FunnelFunctionsLibrary } from 'features/admin/prompt-management/prompt-management.types';
import { FunnelFunctions, FunnelTypes } from 'features/co-pilot/co-pilot.types';
import PaiDotLoader from 'features/ui/base-components/pai-dot-loader';
import { useUserData } from 'hooks/use-user-data';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useErrorStore from 'stores/error-store';
import { Shadows } from 'theme';
import { makeStyles } from 'tss-react/mui';

const PromptManagement: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { role } = useUserData();

  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const [loading, setLoading] = useState(false);
  const [libraries, setLibraries] = useState<FunnelFunctionsLibrary[]>([]);

  const [addLibraryDialog, setAddLibraryDialog] = useState<{
    open: boolean;
    loading: boolean;
    libraryName: string;
    selectedFunnelType: FunnelTypes;
  }>({
    open: false,
    loading: false,
    libraryName: '',
    selectedFunnelType: FunnelTypes.ACTIVE_SOURCING_CO_PILOT,
  });
  const [deleteLibraryDialog, setDeleteLibraryDialog] = useState<{ open: boolean; loading: boolean; library: FunnelFunctionsLibrary | undefined }>({
    open: false,
    loading: false,
    library: undefined,
  });
  const [editLibraryDialog, setEditLibraryDialog] = useState<{
    open: boolean;
    loading: boolean;
    library: FunnelFunctionsLibrary | undefined;
    libraryName: string;
    selectedFunnelType: FunnelTypes;
  }>({
    open: false,
    loading: false,
    library: undefined,
    libraryName: '',
    selectedFunnelType: FunnelTypes.ACTIVE_SOURCING_CO_PILOT,
  });
  const [addFunctionToLibraryDialog, setAddFunctionToLibraryDialog] = useState<{
    open: boolean;
    loading: boolean;
    library: FunnelFunctionsLibrary | undefined;
    functionName: string;
    prompt: string;
  }>({
    open: false,
    loading: false,
    library: undefined,
    functionName: '',
    prompt: '',
  });
  const [deleteFunctionDialog, setDeleteFunctionDialog] = useState<{
    open: boolean;
    loading: boolean;
    library: FunnelFunctionsLibrary | undefined;
    functionName: FunnelFunctions | undefined;
    functionIndex: number | undefined;
  }>({
    open: false,
    loading: false,
    library: undefined,
    functionName: undefined,
    functionIndex: undefined,
  });
  const [editFunctionDialog, setEditFunctionDialog] = useState<{
    open: boolean;
    loading: boolean;
    library: FunnelFunctionsLibrary | undefined;
    functionIndex: number | undefined;
    functionName: string;
    prompt: string;
  }>({ open: false, loading: false, library: undefined, functionIndex: undefined, functionName: '', prompt: '' });

  const [moveFunctionLoading, setMoveFunctionLoading] = useState(false);

  const fetchFunctionLibraries = async (disableLoading: boolean) => {
    if (!disableLoading) {
      setLoading(true);
    }

    const res = await getFunnelFunctionLibraries(undefined);

    if (!res.error) {
      setLibraries(res.linkedInFunnelFunctionsLibraries);
    } else {
      handleOpenErrorDialog(res?.error, '/ai/function-libraries', 'get');
    }

    if (!disableLoading) {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFunctionLibraries(false);
  }, []);

  /**
   * Add library dialog handlers
   */

  const handleOpenAddLibraryDialog = () => {
    setAddLibraryDialog((prev) => ({ ...prev, open: true }));
  };

  const handleCloseAddLibraryDialog = () => {
    setAddLibraryDialog((prev) => ({ ...prev, open: false, loading: false }));
  };

  useEffect(() => {
    if (!addLibraryDialog.open) {
      setTimeout(() => {
        setAddLibraryDialog((prev) => ({ ...prev, libraryName: '', selectedFunnelType: FunnelTypes.ACTIVE_SOURCING_CO_PILOT }));
      }, 225);
    }
  }, [addLibraryDialog.open]);

  const handleAddLibraryNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAddLibraryDialog((prev) => ({ ...prev, libraryName: event.target.value }));
  };

  const handleAddSelectedFunnelTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAddLibraryDialog((prev) => ({ ...prev, selectedFunnelType: event.target.value as FunnelTypes }));
  };

  const handleAddLibrary = async () => {
    setAddLibraryDialog((prev) => ({ ...prev, loading: true }));

    const res = await createFunnelFunctionLibrary(addLibraryDialog.libraryName, addLibraryDialog.selectedFunnelType);

    if (!res.error) {
      fetchFunctionLibraries(true);
      handleCloseAddLibraryDialog();
    } else {
      setAddLibraryDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/ai/function-library', 'post');
    }
  };

  /**
   * Delete library dialog handlers
   */

  const handleOpenDeleteLibraryDialog = (library: FunnelFunctionsLibrary) => {
    return () => {
      setDeleteLibraryDialog((prev) => ({ ...prev, open: true, library }));
    };
  };

  const handleCloseDeleteLibraryDialog = () => {
    setDeleteLibraryDialog((prev) => ({ ...prev, open: false, loading: false }));
  };

  useEffect(() => {
    if (!deleteLibraryDialog.open) {
      setTimeout(() => {
        setDeleteLibraryDialog((prev) => ({ ...prev, library: undefined }));
      }, 225);
    }
  }, [deleteLibraryDialog.open]);

  const handleDeleteLibrary = async () => {
    if (_.isNil(deleteLibraryDialog.library)) return;

    setDeleteLibraryDialog((prev) => ({ ...prev, loading: true }));

    const res = await deleteFunnelFunctionLibrary(deleteLibraryDialog.library._id);

    if (!res.error) {
      fetchFunctionLibraries(true);
      handleCloseDeleteLibraryDialog();
    } else {
      setDeleteLibraryDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/ai/function-library/:id', 'delete');
    }
  };

  /**
   * Edit library dialog handlers
   */

  const handleOpenEditLibraryDialog = (library: FunnelFunctionsLibrary) => {
    return () => {
      setEditLibraryDialog((prev) => ({ ...prev, open: true, library, libraryName: library.name, selectedFunnelType: library.type }));
    };
  };

  const handleCloseEditLibraryDialog = () => {
    setEditLibraryDialog((prev) => ({
      ...prev,
      open: false,
      loading: false,
    }));
  };

  useEffect(() => {
    if (!editLibraryDialog.open) {
      setTimeout(() => {
        setEditLibraryDialog((prev) => ({ ...prev, library: undefined, libraryName: '', selectedFunnelType: FunnelTypes.ACTIVE_SOURCING_CO_PILOT }));
      }, 225);
    }
  }, [editLibraryDialog.open]);

  const handleEditLibraryNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditLibraryDialog((prev) => ({ ...prev, libraryName: event.target.value }));
  };

  const handleEditSelectedFunnelTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditLibraryDialog((prev) => ({ ...prev, selectedFunnelType: event.target.value as FunnelTypes }));
  };

  const handleEditLibrary = async () => {
    if (_.isNil(editLibraryDialog.library)) return;

    setEditLibraryDialog((prev) => ({ ...prev, loading: true }));

    const res = await updateFunnelFunctionLibrary(editLibraryDialog.library._id, {
      name: editLibraryDialog.libraryName,
      type: editLibraryDialog.selectedFunnelType,
    });

    if (!res.error) {
      fetchFunctionLibraries(true);
      handleCloseEditLibraryDialog();
    } else {
      setEditLibraryDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/ai/function-library/:id', 'put');
    }
  };

  /**
   * Add function to library handlers
   */

  const handleOpenAddFunctionToLibraryDialog = (library: FunnelFunctionsLibrary) => {
    return () => {
      setAddFunctionToLibraryDialog((prev) => ({ ...prev, open: true, library }));
    };
  };

  const handleCloseAddFunctionToLibraryDialog = () => {
    setAddFunctionToLibraryDialog((prev) => ({ ...prev, open: false, loading: false }));
  };

  useEffect(() => {
    if (!addFunctionToLibraryDialog.open) {
      setTimeout(() => {
        setAddFunctionToLibraryDialog((prev) => ({ ...prev, library: undefined, functionName: '', prompt: '' }));
      }, 225);
    }
  }, [addFunctionToLibraryDialog.open]);

  const handleAddFunctionNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAddFunctionToLibraryDialog((prev) => ({ ...prev, functionName: event.target.value }));
  };

  const handleAddPromptChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAddFunctionToLibraryDialog((prev) => ({ ...prev, prompt: event.target.value }));
  };

  const handleAddFunction = async () => {
    if (_.isNil(addFunctionToLibraryDialog.library)) return;

    setAddFunctionToLibraryDialog((prev) => ({ ...prev, loading: true }));

    const functions = _.cloneDeep(addFunctionToLibraryDialog.library.functions);
    functions.push({ functionName: addFunctionToLibraryDialog.functionName as FunnelFunctions, prompt: addFunctionToLibraryDialog.prompt });

    const res = await updateFunnelFunctionLibrary(addFunctionToLibraryDialog.library._id, { functions });

    if (!res.error) {
      fetchFunctionLibraries(true);
      handleCloseAddFunctionToLibraryDialog();
    } else {
      setAddFunctionToLibraryDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/ai/function-library/:id', 'put');
    }
  };

  /**
   * Delete function dialog handlers
   */

  const handleOpenDeleteFunctionDialog = (library: FunnelFunctionsLibrary, functionIndex: number) => {
    return () => {
      const functionName = library.functions[functionIndex].functionName;
      setDeleteFunctionDialog((prev) => ({ ...prev, open: true, library, functionName, functionIndex }));
    };
  };

  const handleCloseDeleteFunctionDialog = () => {
    setDeleteFunctionDialog((prev) => ({ ...prev, open: false, loading: false }));
  };

  useEffect(() => {
    if (!deleteFunctionDialog.open) {
      setTimeout(() => {
        setDeleteFunctionDialog((prev) => ({ ...prev, library: undefined, functionName: undefined, functionIndex: undefined }));
      }, 225);
    }
  }, [deleteFunctionDialog.open]);

  const handleDeleteFunction = async () => {
    if (_.isNil(deleteFunctionDialog.library) || _.isNil(deleteFunctionDialog.functionIndex)) return;

    setDeleteFunctionDialog((prev) => ({ ...prev, loading: true }));

    const functions = _.cloneDeep(deleteFunctionDialog.library.functions);
    functions.splice(deleteFunctionDialog.functionIndex, 1);

    const res = await updateFunnelFunctionLibrary(deleteFunctionDialog.library._id, { functions });

    if (!res.error) {
      fetchFunctionLibraries(true);
      handleCloseDeleteFunctionDialog();
    } else {
      setDeleteFunctionDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/ai/function-library/:id', 'put');
    }
  };

  /**
   * Edit function dialog handlers
   */

  const handleOpenEditFunctionDialog = (library: FunnelFunctionsLibrary, functionIndex: number) => {
    return () => {
      const funnelFunction = library.functions[functionIndex];
      setEditFunctionDialog((prev) => ({
        ...prev,
        open: true,
        library,
        functionIndex,
        functionName: funnelFunction.functionName,
        prompt: funnelFunction.prompt,
      }));
    };
  };

  const handleCloseEditFunctionDialog = () => {
    setEditFunctionDialog((prev) => ({ ...prev, open: false, loading: false }));
  };

  useEffect(() => {
    if (!editFunctionDialog.open) {
      setTimeout(() => {
        setEditFunctionDialog((prev) => ({ ...prev, library: undefined, functionIndex: undefined, functionName: '', prompt: '' }));
      }, 225);
    }
  }, [editFunctionDialog.open]);

  const handleEditFunctionNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditFunctionDialog((prev) => ({ ...prev, functionName: event.target.value }));
  };

  const handleEditPromptChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditFunctionDialog((prev) => ({ ...prev, prompt: event.target.value }));
  };

  const handleEditFunction = async () => {
    if (_.isNil(editFunctionDialog.library) || _.isNil(editFunctionDialog.functionIndex)) return;

    setEditFunctionDialog((prev) => ({ ...prev, loading: true }));

    const functions = _.cloneDeep(editFunctionDialog.library.functions);
    const currentFunction = functions[editFunctionDialog.functionIndex];
    currentFunction.functionName = editFunctionDialog.functionName as FunnelFunctions;
    currentFunction.prompt = editFunctionDialog.prompt;

    const res = await updateFunnelFunctionLibrary(editFunctionDialog.library._id, { functions });

    if (!res.error) {
      fetchFunctionLibraries(true);
      handleCloseEditFunctionDialog();
    } else {
      setEditFunctionDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/ai/function-library/:id', 'put');
    }
  };

  /**
   * Move function handlers
   */

  const handleMoveFunction = (library: FunnelFunctionsLibrary, functionIndex: number, direction: -1 | 1) => {
    return async () => {
      const newIndex = functionIndex + direction;
      const functions = _.clone(library.functions);

      if (newIndex >= 0 && newIndex < functions.length) {
        const [movedItem] = functions.splice(functionIndex, 1);
        functions.splice(newIndex, 0, movedItem);
      }

      setMoveFunctionLoading(true);

      const res = await updateFunnelFunctionLibrary(library._id, { functions });

      if (!res.error) {
        await fetchFunctionLibraries(true);
      } else {
        handleOpenErrorDialog(res?.error, '/ai/function-library/:id', 'put');
      }

      setMoveFunctionLoading(false);
    };
  };

  if (role !== Roles.OWNER) {
    return <Typography p={4}>{t('noPermission', 'You do not have permission to access this page.')}</Typography>;
  }

  return (
    <>
      <div className={classes.promptManagement}>
        <AdminSubpageHeader title={t('promptManagement')} />
        <div className={classes.content}>
          <Stack className={classes.promptLibraries} gap={2}>
            {loading ? (
              <PaiDotLoader />
            ) : (
              <Stack className={classes.innerContent} direction={'row'} flexWrap={'nowrap'}>
                <PromptLibraries
                  libraries={libraries}
                  moveFunctionLoading={moveFunctionLoading}
                  handleOpenAddLibraryDialog={handleOpenAddLibraryDialog}
                  handleOpenDeleteLibraryDialog={handleOpenDeleteLibraryDialog}
                  handleOpenEditLibraryDialog={handleOpenEditLibraryDialog}
                  handleOpenAddFunctionToLibraryDialog={handleOpenAddFunctionToLibraryDialog}
                  handleOpenDeleteFunctionDialog={handleOpenDeleteFunctionDialog}
                  handleOpenEditFunctionDialog={handleOpenEditFunctionDialog}
                  handleMoveFunction={handleMoveFunction}
                />
              </Stack>
            )}
          </Stack>
        </div>
      </div>

      <AddLibraryDialog
        open={addLibraryDialog.open}
        loading={addLibraryDialog.loading}
        libraryName={addLibraryDialog.libraryName}
        selectedFunnelType={addLibraryDialog.selectedFunnelType}
        handleLibraryNameChange={handleAddLibraryNameChange}
        handleSelectedFunnelTypeChange={handleAddSelectedFunnelTypeChange}
        handleClose={handleCloseAddLibraryDialog}
        handleAddLibrary={handleAddLibrary}
      />
      <DeleteLibraryDialog
        open={deleteLibraryDialog.open}
        loading={deleteLibraryDialog.loading}
        libraryName={deleteLibraryDialog.library?.name}
        handleClose={handleCloseDeleteLibraryDialog}
        handleDeleteLibrary={handleDeleteLibrary}
      />
      <EditLibraryDialog
        open={editLibraryDialog.open}
        loading={editLibraryDialog.loading}
        libraryName={editLibraryDialog.libraryName}
        selectedFunnelType={editLibraryDialog.selectedFunnelType}
        handleLibraryNameChange={handleEditLibraryNameChange}
        handleSelectedFunnelTypeChange={handleEditSelectedFunnelTypeChange}
        handleClose={handleCloseEditLibraryDialog}
        handleEditLibrary={handleEditLibrary}
      />
      <AddFunctionToLibraryDialog
        open={addFunctionToLibraryDialog.open}
        loading={addFunctionToLibraryDialog.loading}
        functionName={addFunctionToLibraryDialog.functionName}
        prompt={addFunctionToLibraryDialog.prompt}
        handleFunctionNameChange={handleAddFunctionNameChange}
        handlePromptChange={handleAddPromptChange}
        handleClose={handleCloseAddFunctionToLibraryDialog}
        handleAddFunction={handleAddFunction}
      />
      <DeleteFunctionDialog
        open={deleteFunctionDialog.open}
        loading={deleteFunctionDialog.loading}
        functionName={deleteFunctionDialog.functionName}
        handleClose={handleCloseDeleteFunctionDialog}
        handleDeleteFunction={handleDeleteFunction}
      />
      <EditFunctionDialog
        open={editFunctionDialog.open}
        loading={editFunctionDialog.loading}
        functionName={editFunctionDialog.functionName}
        prompt={editFunctionDialog.prompt}
        handleFunctionNameChange={handleEditFunctionNameChange}
        handlePromptChange={handleEditPromptChange}
        handleClose={handleCloseEditFunctionDialog}
        handleEditFunction={handleEditFunction}
      />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  promptManagement: {
    padding: '8px',
    background: theme.palette.backgrounds.main,
    height: 'calc(100vh - 60px - 32px)',
    display: 'flex',
    flexDirection: 'column',

    [theme.breakpoints.down('xl')]: {
      height: `calc((100vh / ${ZoomLevels.XL}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1300)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('lg')]: {
      height: `calc((100vh / ${ZoomLevels.LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1000)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_MD}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('md')]: {
      height: `calc((100vh / ${ZoomLevels.MD}) - 60px - 32px)`,
    },
  },
  content: {
    background: '#FFF',
    boxShadow: Shadows.GREY_SHADOW_LIGHT,
    padding: '12px 16px 16px',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  promptLibraries: {
    flex: 1,
    minHeight: 0,
  },
  innerContent: {
    maxHeight: '100%',
    border: `2px solid ${theme.palette.backgrounds.main}`,
  },
}));

export default PromptManagement;
